import React from 'react'
import { Link, Navigate } from 'react-router-dom'
import { AdjustmentsHorizontalIcon, ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../context/AuthContext'
import Loader from '../components/Loader'
import useDocumentTitle from '../hooks/useDocumentTitle'
import KitPreferencesSection from '../components/account/KitPreferencesSection'
import { useFlash } from './Account'

/**
 * Preferencje doboru — własna strona, nie zakładka w ustawieniach konta.
 *
 * Wcześniej siedziały jako jedna z siedmiu zakładek `/konto`, między „Firma"
 * a „Hasło". To najczęściej zmieniana rzecz na koncie (marki, których sklep
 * faktycznie używa, sterują całym asystentem doboru), a leżała najgłębiej —
 * dwa kliknięcia i przeczytanie listy zakładek. Teraz jest pierwszą pozycją
 * w menu konta.
 *
 * `KitPreferencesSection` bierze z konta wyłącznie `setFlash`, więc ta strona
 * NIE pobiera profilu — inaczej dokładałaby zapytanie do API tylko po to, żeby
 * wypisać adres e-mail w nagłówku.
 */
const Preferencje = () => {
  useDocumentTitle('Preferencje doboru')
  const { isAuthenticated, loading: authLoading } = useAuth()
  const [flash, setFlash] = useFlash()

  // Ta sama kolejność co w `Account`: dopóki nie wiemy, kto jest zalogowany,
  // NIE przekierowujemy — inaczej odświeżenie strony wyrzucałoby zalogowanego
  // na formularz logowania.
  if (authLoading) {
    return <div className="flex justify-center py-24"><Loader /></div>
  }
  if (!isAuthenticated) {
    return <Navigate to="/logowanie" state={{ from: '/preferencje' }} replace />
  }

  return (
    // `max-w-6xl` jak na koncie: sekcja mieści trzy kafelki modułów w rzędzie,
    // przy węższym kontenerze łamią się na 2+1.
    <div className="px-4 md:px-8 lg:px-12 py-8 max-w-6xl mx-auto">
      <Link
        to="/konto"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Moje konto
      </Link>

      <div className="mt-3 mb-6 flex items-start gap-3" data-samouczek="preferencje">
        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-blue-50 flex-shrink-0">
          <AdjustmentsHorizontalIcon className="w-6 h-6 text-blue-600" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Preferencje doboru</h1>
          <p className="mt-1 text-sm text-gray-500">
            Marki i sprzęt, z których mamy składać zestawy. Asystent doboru bierze te
            ustawienia pod uwagę przy każdym wyliczeniu.
          </p>
        </div>
      </div>

      {flash && (
        <div className="mb-5 flex items-center gap-2.5 rounded-lg bg-emerald-50 border border-emerald-100 p-3">
          <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm text-emerald-800">{flash}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sm:p-6">
        <KitPreferencesSection setFlash={setFlash} />
      </div>
    </div>
  )
}

export default Preferencje
